import React from "react";
import { useTranslation } from "react-i18next";
import { ServicesURL, onErrorMessage } from "@utils/utilsApi";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { OutcomesInterface } from "@components/ModalCst";
import { buildUserPayload, UserWizardData } from "./types";
import {
  diffStaticGroups,
  hasStaticGroupsChanges,
  updateUserStaticGroups,
} from "../updateUserStaticGroups";

const USER_STEP = "user";
const GROUPS_STEP = "groups";

interface UseSaveUserArgs {
  mode: "create" | "edit";
  corporateId: string;
  userId?: string;
  detail?: any;
  initialStaticGroupIds: (string | number)[];
}

const extractUserId = (response: any): string | number | undefined => {
  const source = response?.output ?? response?.data ?? response;
  return source?.id ?? source?.user_id ?? source?.users_id ?? source?.userId;
};

export const useSaveUser = ({
  mode,
  corporateId,
  userId,
  detail,
  initialStaticGroupIds,
}: UseSaveUserArgs) => {
  const { t } = useTranslation("user-wizard-v2");
  const [outcome, setOutcome] = React.useState<OutcomesInterface>({});
  const [openOutcome, setOpenOutcome] = React.useState(false);

  const save = async (data: UserWizardData) => {
    const isEdit = mode === "edit";
    const diff = diffStaticGroups(initialStaticGroupIds, data.staticGroupIds);
    const withGroups = hasStaticGroupsChanges(diff);

    const next: OutcomesInterface = {
      [USER_STEP]: {
        code: undefined,
        message: t("outcome.loading"),
        title: isEdit ? t("outcome.updateUser") : t("outcome.createUser"),
      },
    };
    if (withGroups) {
      next[GROUPS_STEP] = {
        code: undefined,
        message: t("outcome.loading"),
        title: t("outcome.assignGroups"),
      };
    }
    setOutcome({ ...next });
    setOpenOutcome(true);

    let resolvedUserId: string | number | undefined = userId;

    try {
      if (isEdit) {
        await fetcher(
          `${ServicesURL.putCustomer}/${userId}?corporate_id=${corporateId}`,
          MethodType.PUT,
          buildUserPayload(data, detail, true),
        );
      } else {
        const response = await fetcher(
          `${ServicesURL.postCustomer}?corporate_id=${corporateId}`,
          MethodType.POST,
          buildUserPayload(data, undefined, false),
        );
        resolvedUserId = extractUserId(response);
      }
      next[USER_STEP].code = "OK";
      next[USER_STEP].message = t("outcome.success");
      setOutcome({ ...next });
    } catch (error) {
      next[USER_STEP].code = "KO";
      next[USER_STEP].message = onErrorMessage(error);
      setOutcome({ ...next });
      return;
    }

    if (!withGroups) return;

    if (resolvedUserId == null || resolvedUserId === "") {
      next[GROUPS_STEP].code = "KO";
      next[GROUPS_STEP].message = t("outcome.missingUserId");
      setOutcome({ ...next });
      return;
    }

    try {
      await updateUserStaticGroups(resolvedUserId, diff);
      next[GROUPS_STEP].code = "OK";
      next[GROUPS_STEP].message = t("outcome.success");
      setOutcome({ ...next });
    } catch (error) {
      next[GROUPS_STEP].code = "KO";
      next[GROUPS_STEP].message = onErrorMessage(error);
      setOutcome({ ...next });
    }
  };

  return { outcome, openOutcome, setOpenOutcome, save };
};
